import React, { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@/hooks";
import { FormProvider } from "react-hook-form";

import Form from "./FormInputs";
import CTASuccessView from "@/components/Molecules/CTASuccessView";

import { DEFAULT_URL_ARGUMENTS } from "@/utils/Constants";

import useAnnualMembershipForm from "./useAnnualMembershipForm";
import useFormHandler from "./useFormHandler";
import usePromoCode from "./usePromoCode";
import { generateUrlArguments } from "@/utils";

const FormContainer = ({
  defaultPackageSlug,
  options,
  pricing,
  handleCallbackFormView,
  showGiftSection,
  //
  showNameField,
  namePlaceholder,
  showPhoneField,
  mobilePlaceholder,
  showEmailField,
  emailPlaceholder,
  showGoalField,
  goalPlaceholder,
  showPromoCodeField,
  promoCodePlaceholder,
  //
  ...rest
}) => {
  const [isGift, setIsGift] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const { promoCodeSuccess: isPromoCodeApplied, promoCodeData: pageData } =
    useAppSelector((state) => state.landingPage);

  const urlsArguments = generateUrlArguments() || DEFAULT_URL_ARGUMENTS;

  const { methods, resetValuesOnToggle, mapUserData } = useAnnualMembershipForm(
    {
      isGift,
      defaultPackageSlug,
      urlsArguments,
      options,
    }
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (isGift) {
      resetValuesOnToggle();
    }
  }, [isGift, resetValuesOnToggle]);

  const { handleBlur, onSubmitCallback } = useFormHandler({
    methods,
    mapUserData,
    pricing,
    setSuccess,
    setIsLoading,
  });

  const _pricing = useMemo(
    () => ({
      ...pricing,
      price: isPromoCodeApplied ? pageData.new_price : pricing?.price,
    }),
    [isPromoCodeApplied, pageData, pricing]
  );

  const {
    promoCodeButtonText,
    applyPromoCode,
    handlePromoCodeChange,
    removeAppliedPromoCode,
  } = usePromoCode({ methods });

  return success ? (
    <CTASuccessView />
  ) : (
    <FormProvider {...methods}>
      <Form
        isLoading={isLoading}
        success={success}
        error={error}
        onSubmitCallback={onSubmitCallback}
        showLabels={false}
        //
        goalOptions={options}
        isGift={isGift}
        setIsGift={setIsGift}
        handleSubmit={handleSubmit}
        control={control}
        errors={errors}
        handleBlur={handleBlur}
        pricing={_pricing}
        handleCallbackFormView={handleCallbackFormView}
        //
        promoCodeButtonText={promoCodeButtonText}
        applyPromoCode={applyPromoCode}
        handlePromoCodeChange={handlePromoCodeChange}
        removeAppliedPromoCode={removeAppliedPromoCode}
        //
        showGiftSection={showGiftSection}
        showNameField={showNameField}
        namePlaceholder={namePlaceholder}
        showPhoneField={showPhoneField}
        mobilePlaceholder={mobilePlaceholder}
        showEmailField={showEmailField}
        emailPlaceholder={emailPlaceholder}
        showGoalField={showGoalField}
        goalPlaceholder={goalPlaceholder}
        showPromoCodeField={showPromoCodeField}
        promoCodePlaceholder={promoCodePlaceholder}
        //
        {...rest}
      />
    </FormProvider>
  );
};

export default FormContainer;
